import React, { useCallback, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react';
import { AlertTriangle, CheckCircle2, ClipboardPlus, Loader2, Minus, Plus, RefreshCw, RotateCcw, Trash2, X } from 'lucide-react';
import { Button } from '../Button';
import type { SigeProject } from './types';
import type { SigePlanFusszeile } from '../../services/pdf/generatorSigePlan';
import { PlotFehler, type PlotAuftrag } from '../../services/plotter/plotTypen';
import { plotTitel } from '../../services/plotter/plotStatus';
import { plotAuftraegeLaden } from '../../services/plotter/plotAuftraege';
import {
    PLOT_NACHTRAG_MAX_KOPIEN, PLOT_NACHTRAG_MAX_MM, PLOT_NACHTRAG_MAX_TAGE_ZURUECK, PLOT_NACHTRAG_MIN_MM,
    heuteBerlin, laengeParsen, meterText, plotDruckNachtraegeLaden, plotDruckNachtragen, plotDruckNachtragLoeschen,
    plotRollenStandLaden,
    type PlotDruckNachtrag, type PlotRollenStand,
} from '../../services/plotter/plotRolle';
import {
    nachtragAbsichtAbgleichen, nachtragAbsichtBeginnen, nachtragAbsichtenAbonnieren, nachtragAbsichtenStand,
    nachtragAbsichtErfolg, nachtragAbsichtFehler, nachtragAbsichtVerwerfen, nachtragLadenBeginnt, nachtragUhrMs,
    nachtragVerwerfenAbMs, NACHTRAG_VERWERFEN_FRIST_MS,
    type NachtragAngaben,
} from './nachtragAbsichten';

/**
 * „Druck nachtragen“: ein Plan, der direkt vom Rechner auf den Plotter ging
 * (nicht über „An Plotter senden“), wird von Hand mit Länge und Kopien
 * erfasst. Er zählt gegen die Papierrolle (SQL Nr. 169/170) und steht in der
 * Druckliste der Admins.
 *
 * Gesperrt wird hier nie — der Druck ist schon passiert. Gegen doppeltes
 * Zählen: ID und Angaben stehen als Absicht außerhalb des Dialogs
 * (nachtragAbsichten.ts) und bleiben bis zur Klärung — auch über Schließen
 * und Neuöffnen. Ein Doppelklick, „Erneut speichern“ oder ein neuer Dialog
 * schicken dieselbe ID, der Server legt nichts doppelt an. Geklärt ist sie
 * durch den Erfolg oder, wenn ihre ID in der Projektliste steht; verworfen
 * werden darf sie erst, wenn eine vollständige Liste nach dem Fehlschlag sie
 * nicht enthält. Während des Speicherns lässt sich der Dialog nicht schließen.
 */

export interface DruckNachtragenDialogProps {
    /** Hauptprojekt (`projects.id`) — der Server prüft den Zugriff darauf. */
    projectId: string;
    sigeProject: Pick<SigeProject, 'id' | 'plan_nr'>;
    /** Wie für Plan-Vorschau und Plotter-Dialog: Projekt-Nr. und -bezeichnung. */
    fusszeile?: SigePlanFusszeile;
    /**
     * Eigene Nutzer-ID (`profiles.id`): Löschen wird nur bei eigenen Nachträgen
     * angeboten. Unbekannt → überall angeboten, der Server entscheidet.
     */
    eigeneNutzerId?: string | null;
    /** Admins dürfen jeden Nachtrag der Firma löschen. */
    istAdmin?: boolean;
    onClose: () => void;
}

const LISTE_LIMIT = 50;
const BEMERKUNG_MAX = 500;
const TITEL_MAX = 200;
/** Der Client kennt kein Zeitlimit: eine hängende Abfrage sperrte sonst jedes weitere Laden. */
const LADEN_FRIST_MS = 15_000;
const MAX_RUNDEN = 3;

const mitFrist = <T,>(p: Promise<T>): Promise<T> => new Promise<T>((ok, nein) => {
    const t = setTimeout(() => nein(new PlotFehler('netz', 'Keine Antwort vom Server — bitte erneut aktualisieren.')), LADEN_FRIST_MS);
    p.then(w => { clearTimeout(t); ok(w); }, (e: unknown) => { clearTimeout(t); nein(e); });
});

const fehlerText = (e: unknown): string => (e instanceof Error ? e.message : String(e));
const mm = (n: number) => Math.round(n).toLocaleString('de-DE');
const uhrzeit = (ms: number) => new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const INPUT = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400';
const LABEL = 'block text-[11px] font-bold text-slate-500 uppercase mb-1';

// ─── Datum ───────────────────────────────────────────────────────────────────

const ISO_TAG = /^\d{4}-\d{2}-\d{2}$/;

/** 'YYYY-MM-DD' minus `tage` Kalendertage. */
const tageZurueck = (tag: string, tage: number): string => {
    const [j, m, t] = tag.split('-').map(Number);
    return new Date(Date.UTC(j, m - 1, t - tage)).toISOString().slice(0, 10);
};

const tagText = (tag: string): string => {
    const [j, m, t] = tag.split('-');
    return `${t}.${m}.${j}`;
};

const datumFormat = new Intl.DateTimeFormat('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric' });
const zeitMs = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const t = Date.parse(iso.trim().replace(' ', 'T'));
    return Number.isFinite(t) ? t : null;
};

// ─── Prüfen ──────────────────────────────────────────────────────────────────

type LaengePruefung = { mm: number; fehler: '' } | { mm: null; fehler: string };

const laengePruefen = (eingabe: string): LaengePruefung => {
    if (!eingabe.trim()) return { mm: null, fehler: 'Bitte die Länge des Blatts eingeben.' };
    const wert = laengeParsen(eingabe);
    if (wert === null) return { mm: null, fehler: 'Keine gültige Länge — z. B. 1,20 m oder 120 cm.' };
    if (wert < PLOT_NACHTRAG_MIN_MM || wert > PLOT_NACHTRAG_MAX_MM) {
        return { mm: null, fehler: `Die Länge muss zwischen ${meterText(PLOT_NACHTRAG_MIN_MM)} und ${meterText(PLOT_NACHTRAG_MAX_MM)} liegen.` };
    }
    return { mm: wert, fehler: '' };
};

const datumPruefen = (tag: string, heute: string, frueheste: string): string => {
    if (!ISO_TAG.test(tag)) return 'Bitte das Druckdatum wählen.';
    if (tag > heute) return 'Das Druckdatum darf nicht in der Zukunft liegen.';
    if (tag < frueheste) return `Höchstens ${PLOT_NACHTRAG_MAX_TAGE_ZURUECK} Tage zurück (ab ${tagText(frueheste)}).`;
    return '';
};

// ─── Liste ───────────────────────────────────────────────────────────────────

/**
 * Gezählte Kopien eines App-Auftrags — dieselben Regeln wie
 * `plot_rolle_rechnen` (SQL Nr. 170), nur für die Anzeige. Wartende zählen
 * hier nicht (noch nicht gedruckt), Abgebrochenes und Abgelaufenes nie.
 */
const gezaehlteKopien = (a: PlotAuftrag): number => {
    switch (a.status) {
        case 'gedruckt': return a.kopien_fertig > 0 ? a.kopien_fertig : a.kopien;
        case 'fehler': return a.kopien_fertig;
        case 'druckt':
        case 'unklar': return a.kopien;
        default: return 0;
    }
};

const APP_STATUS: Partial<Record<PlotAuftrag['status'], string>> = {
    gedruckt: 'gedruckt', fehler: 'Fehler', druckt: 'druckt', unklar: 'unklar',
};

interface ListenZeile {
    schluessel: string;
    quelle: 'app' | 'nachtrag';
    zeitpunkt: string;
    ms: number;
    titel: string;
    laengeMm: number;
    kopien: number;
    gesamtMm: number;
    status: string;
    wer: string | null;
    bemerkung: string | null;
    nachtrag: PlotDruckNachtrag | null;
}

const zeilenBauen = (nachtraege: PlotDruckNachtrag[], auftraege: PlotAuftrag[]): ListenZeile[] => {
    const zeilen: ListenZeile[] = nachtraege.map(n => ({
        schluessel: `nachtrag:${n.id}`,
        quelle: 'nachtrag',
        zeitpunkt: n.gedruckt_am,
        ms: zeitMs(n.gedruckt_am) ?? 0,
        titel: n.titel,
        laengeMm: n.laenge_mm,
        kopien: n.kopien,
        gesamtMm: n.laenge_mm * n.kopien,
        status: 'nachgetragen',
        wer: n.erstellt_von_name,
        bemerkung: n.bemerkung,
        nachtrag: n,
    }));
    for (const a of auftraege) {
        const kopien = gezaehlteKopien(a);
        if (!(kopien > 0 && a.hoehe_mm > 0)) continue;
        const zeitpunkt = a.abgeholt_am ?? a.erstellt_am;
        zeilen.push({
            schluessel: `app:${a.id}`,
            quelle: 'app',
            zeitpunkt,
            ms: zeitMs(zeitpunkt) ?? 0,
            titel: a.titel,
            laengeMm: a.hoehe_mm,
            kopien,
            gesamtMm: a.hoehe_mm * kopien,
            status: APP_STATUS[a.status] ?? a.status,
            wer: a.erstellt_von_name,
            bemerkung: a.meldung,
            nachtrag: null,
        });
    }
    return zeilen.sort((x, y) => y.ms - x.ms);
};

export const DruckNachtragenDialog: React.FC<DruckNachtragenDialogProps> = ({
    projectId, sigeProject, fusszeile, eigeneNutzerId, istAdmin = false, onClose,
}) => {
    // Heute und die Grenzen einmal beim Öffnen — nicht bei jedem Rendern neu.
    const [heute] = useState(() => heuteBerlin());
    const frueheste = tageZurueck(heute, PLOT_NACHTRAG_MAX_TAGE_ZURUECK);

    // Eingaben für einen NEUEN Nachtrag. Steht eine unbestätigte Absicht,
    // zeigt das Formular deren Angaben, gesperrt.
    const [titel, setTitel] = useState(() => plotTitel(sigeProject.plan_nr, fusszeile?.projektNummer, fusszeile?.projektName));
    const [laenge, setLaenge] = useState('');
    const [kopien, setKopien] = useState(1);
    const [datum, setDatum] = useState(heute);
    const [bemerkung, setBemerkung] = useState('');
    const [versucht, setVersucht] = useState(false);
    const [erfolg, setErfolg] = useState<{ text: string; schonDa: boolean } | null>(null);

    const absichten = useSyncExternalStore(nachtragAbsichtenAbonnieren, nachtragAbsichtenStand, nachtragAbsichtenStand);
    const absicht = absichten.get(projectId) ?? null;
    const laeuft = absicht?.laeuft ?? false;

    const [nachtraege, setNachtraege] = useState<PlotDruckNachtrag[] | null>(null);
    const [auftraege, setAuftraege] = useState<PlotAuftrag[] | null>(null);
    // undefined = noch nicht geladen; null = SQL Nr. 169/170 fehlt.
    const [stand, setStand] = useState<PlotRollenStand | null | undefined>(undefined);
    const [listeFehler, setListeFehler] = useState('');
    const [standMs, setStandMs] = useState(0);
    const [manuellLaedt, setManuellLaedt] = useState(false);

    const [loeschenFrage, setLoeschenFrage] = useState<string | null>(null);
    const [loescht, setLoescht] = useState<string | null>(null);
    const [loeschenFehler, setLoeschenFehler] = useState<{ id: string; text: string } | null>(null);

    const aktivRef = useRef(true);
    const loeschtRef = useRef(false);
    const laufRef = useRef<Promise<void> | null>(null);
    const nochmalRef = useRef(false);
    // Zählt eigene Änderungen: ein Laden, das vorher begann, brächte den alten Stand.
    const standRef = useRef(0);

    const idTitel = useId();
    const idLaenge = useId();
    const idDatum = useId();
    const idBemerkung = useId();

    useEffect(() => {
        aktivRef.current = true;
        return () => { aktivRef.current = false; };
    }, []);

    /** Formular für den nächsten Druck: Länge leer, eine Kopie, keine Bemerkung. */
    const formularLeeren = useCallback(() => {
        setLaenge('');
        setKopien(1);
        setBemerkung('');
        setVersucht(false);
    }, []);

    const laden = useCallback((): Promise<void> => {
        if (laufRef.current) {
            nochmalRef.current = true;
            return laufRef.current;
        }
        const lauf = (async () => {
            let runden = 0;
            do {
                runden += 1;
                nochmalRef.current = false;
                const vorher = standRef.current;
                // Vor dem Laden: Nummer und Uhr. Eine Liste, die vor einem Fehlschlag
                // oder innerhalb der Frist danach begann, beweist nichts.
                const marke = nachtragLadenBeginnt();
                const [n, a, s] = await Promise.allSettled([
                    mitFrist(plotDruckNachtraegeLaden({ projectId, limit: LISTE_LIMIT })),
                    mitFrist(plotAuftraegeLaden({ projectId, limit: LISTE_LIMIT })),
                    mitFrist(plotRollenStandLaden()),
                ]);
                // Offene Absicht gegen die Liste: darin = angekommen. Nur eine Liste
                // unter dem Limit ist vollständig — sonst beweist ein Fehlen nichts.
                const angekommen = n.status === 'fulfilled'
                    && nachtragAbsichtAbgleichen(projectId, n.value.map(x => x.id), marke, n.value.length < LISTE_LIMIT) === 'angekommen';
                if (!aktivRef.current) return;
                if (angekommen) {
                    setErfolg({ text: 'Der letzte Nachtrag ist gespeichert — er steht unten in der Liste.', schonDa: false });
                    formularLeeren();
                }
                if (s.status === 'fulfilled') setStand(s.value);
                if (vorher !== standRef.current) {
                    nochmalRef.current = true;
                    continue;
                }
                const fehler = [n, a].find((r): r is PromiseRejectedResult => r.status === 'rejected');
                if (n.status === 'fulfilled') setNachtraege(n.value);
                if (a.status === 'fulfilled') setAuftraege(a.value);
                if (fehler) {
                    setListeFehler(fehlerText(fehler.reason));
                } else {
                    setListeFehler('');
                    setStandMs(Date.now());
                }
            } while (nochmalRef.current && aktivRef.current && runden < MAX_RUNDEN);
        })().finally(() => { laufRef.current = null; });
        laufRef.current = lauf;
        return lauf;
    }, [projectId, formularLeeren]);

    useEffect(() => { void laden(); }, [laden]);

    // Endet ein Versuch zu diesem Projekt (auch aus einem früheren Dialog):
    // verschwunden = gespeichert; sonst gescheitert → nachladen, damit die
    // Liste zeigt, ob er doch angekommen ist (und erst dann Verwerfen frei wird).
    useEffect(() => {
        let vorher = nachtragAbsichtenStand().get(projectId);
        return nachtragAbsichtenAbonnieren(() => {
            const jetzt = nachtragAbsichtenStand().get(projectId);
            const beendet = !!vorher?.laeuft && !jetzt?.laeuft;
            const gespeichert = beendet && !jetzt;
            vorher = jetzt;
            if (!beendet || !aktivRef.current) return;
            if (gespeichert) {
                setErfolg({ text: 'Der Nachtrag ist gespeichert — er steht unten in der Liste.', schonDa: false });
                formularLeeren();
            }
            void laden();
        });
    }, [projectId, laden, formularLeeren]);

    // Nach einem Fehlschlag: sobald die Frist um ist, die Liste noch einmal
    // laden — erst diese kann zeigen, dass der Nachtrag nicht angekommen ist.
    const verwerfenAbMs = nachtragVerwerfenAbMs(absicht);
    useEffect(() => {
        if (verwerfenAbMs === null) return;
        // Etwas Luft: der Timer darf nicht vor der Frist feuern.
        const t = setTimeout(() => { void laden(); }, Math.max(0, verwerfenAbMs - nachtragUhrMs()) + 250);
        return () => clearTimeout(t);
    }, [verwerfenAbMs, laden]);

    const manuellAktualisieren = () => {
        if (manuellLaedt) return;
        setManuellLaedt(true);
        const start = Date.now();
        void laden().finally(() => {
            // Kurz sichtbar lassen — sonst wirkt der Knopf, als täte er nichts.
            const rest = Math.max(0, 600 - (Date.now() - start));
            setTimeout(() => { if (aktivRef.current) setManuellLaedt(false); }, rest);
        });
    };

    // Schließen erst, wenn kein Speichern läuft — sonst bliebe ungewiss, ob der Nachtrag ankam.
    useEffect(() => {
        const taste = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            if (!laeuft) onClose();
        };
        document.addEventListener('keydown', taste, true);
        return () => document.removeEventListener('keydown', taste, true);
    }, [onClose, laeuft]);

    const laengeGeprueft = laengePruefen(laenge);
    const datumFehler = datumPruefen(datum, heute, frueheste);
    const zeigeLaengeFehler = !absicht && laengeGeprueft.mm === null && (versucht || laenge.trim() !== '');
    const gueltig = laengeGeprueft.mm !== null && !datumFehler;

    const absenden = async () => {
        // Gilt die Absicht aus dem Rendern noch? Ist sie inzwischen geklärt, nichts tun —
        // ein Klick auf „Erneut speichern“ darf nie eine neue Absicht anlegen.
        const aktuell = nachtragAbsichtenStand().get(projectId) ?? null;
        if (absicht && !aktuell) return;
        let angaben: NachtragAngaben | null = aktuell?.angaben ?? null;
        if (!angaben) {
            setVersucht(true);
            const laengeMm = laengeGeprueft.mm;
            if (laengeMm === null || datumFehler) return;
            angaben = {
                sigeProjektId: sigeProject.id || null,
                titel: titel.trim(),
                laengeText: laenge.trim(),
                laengeMm,
                kopien,
                datum,
                bemerkung: bemerkung.trim(),
            };
        }
        // Einmal je Absicht; ein zweiter Klick oder ein neuer Versuch schickt dieselbe.
        const a = nachtragAbsichtBeginnen(projectId, angaben, () => crypto.randomUUID());
        if (!a) return;
        setErfolg(null);
        try {
            const neu = await plotDruckNachtragen({
                id: a.id,
                projectId,
                sigeProjektId: a.angaben.sigeProjektId,
                titel: a.angaben.titel,
                laengeMm: a.angaben.laengeMm,
                kopien: a.angaben.kopien,
                datum: a.angaben.datum,
                bemerkung: a.angaben.bemerkung || null,
            });
            if (aktivRef.current) {
                standRef.current += 1;
                setNachtraege(alt => [neu, ...(alt ?? []).filter(x => x.id !== neu.id)]);
            }
            // Erst nach dem Erfolg beginnt die nächste Absicht; das Nachladen stößt der Zuhörer an.
            nachtragAbsichtErfolg(projectId, a.id);
            if (!aktivRef.current) return;
            // Gleiche ID schon vorhanden (erster Versuch kam doch an): der Server
            // gibt die gespeicherte Zeile zurück — es gelten deren Angaben.
            const schonDa = Math.abs(neu.laenge_mm - a.angaben.laengeMm) > 0.05 || neu.kopien !== a.angaben.kopien;
            setErfolg({
                text: `Nachgetragen: ${neu.kopien} × ${meterText(neu.laenge_mm)} = ${meterText(neu.laenge_mm * neu.kopien)}.`,
                schonDa,
            });
        } catch (e) {
            nachtragAbsichtFehler(projectId, a.id, fehlerText(e));
        }
    };

    /** Nur eine nachweislich nicht angekommene Absicht; ihre Angaben kommen ins Formular zurück. */
    const verwerfen = () => {
        if (!absicht) return;
        const g = absicht.angaben;
        if (!nachtragAbsichtVerwerfen(projectId, absicht.id)) return;
        setTitel(g.titel);
        setLaenge(g.laengeText);
        setKopien(g.kopien);
        setDatum(g.datum);
        setBemerkung(g.bemerkung);
        setVersucht(false);
        setErfolg(null);
    };

    const loeschen = async (id: string) => {
        if (loeschtRef.current) return;
        loeschtRef.current = true;
        setLoescht(id);
        setLoeschenFehler(null);
        try {
            await plotDruckNachtragLoeschen(id);
            if (!aktivRef.current) return;
            setLoeschenFrage(null);
            standRef.current += 1;
            setNachtraege(alt => (alt ?? []).filter(x => x.id !== id));
            void laden();
        } catch (e) {
            if (aktivRef.current) setLoeschenFehler({ id, text: fehlerText(e) });
        } finally {
            loeschtRef.current = false;
            if (aktivRef.current) setLoescht(null);
        }
    };

    const darfLoeschen = (n: PlotDruckNachtrag): boolean =>
        istAdmin || !eigeneNutzerId || n.erstellt_von === eigeneNutzerId;

    const zeilen = nachtraege !== null || auftraege !== null ? zeilenBauen(nachtraege ?? [], auftraege ?? []) : null;
    const rolleLeer = !!stand?.rolle && stand.rest_mm !== null && stand.rest_mm < 0;

    // Angezeigt: die Angaben der offenen Absicht, sonst das Formular.
    const g = absicht?.angaben ?? null;
    const zeigeTitel = g ? g.titel : titel;
    const zeigeLaenge = g ? g.laengeText : laenge;
    const zeigeKopien = g ? g.kopien : kopien;
    const zeigeDatum = g ? g.datum : datum;
    const zeigeBemerkung = g ? g.bemerkung : bemerkung;
    const zeigeMm = g ? g.laengeMm : laengeGeprueft.mm;
    const gesamtMm = zeigeMm !== null ? zeigeMm * zeigeKopien : null;
    const gesperrt = absicht !== null;

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4 animate-fade-in">
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="druck-nachtragen-titel"
                className="bg-white w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden"
            >
                <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-start gap-3">
                    <div className="p-2 rounded-xl bg-blue-50 text-blue-600 shrink-0"><ClipboardPlus className="w-5 h-5" /></div>
                    <div className="min-w-0 flex-1">
                        <h2 id="druck-nachtragen-titel" className="text-lg font-bold text-slate-800">Druck nachtragen</h2>
                        <p className="text-xs text-slate-500">Plan direkt vom Rechner gedruckt — zählt gegen die Papierrolle.</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={laeuft}
                        aria-label="Schließen"
                        title={laeuft ? 'Erst nach dem Speichern schließen' : 'Schließen'}
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center -m-1 hover:bg-slate-200 rounded-lg text-slate-500 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {stand?.rolle && stand.frei_mm !== null && (
                        <p className="text-xs text-slate-600">
                            Papierrolle: {stand.frei_mm >= 0 ? `noch ${meterText(stand.frei_mm)} frei` : 'laut Zählung leer'}
                        </p>
                    )}
                    {rolleLeer && (
                        <div className="flex items-start gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50 text-xs text-amber-800">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>Die Rolle ist laut Zählung leer — bitte Admin informieren.</span>
                        </div>
                    )}

                    <div className="space-y-3">
                        <div>
                            <label htmlFor={idTitel} className={LABEL}>Titel</label>
                            <input
                                id={idTitel}
                                type="text"
                                value={zeigeTitel}
                                maxLength={TITEL_MAX}
                                onChange={(e) => { setTitel(e.target.value); setErfolg(null); }}
                                disabled={gesperrt}
                                className={INPUT}
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label htmlFor={idLaenge} className={LABEL}>Blattlänge</label>
                                <input
                                    id={idLaenge}
                                    type="text"
                                    inputMode="decimal"
                                    autoComplete="off"
                                    value={zeigeLaenge}
                                    placeholder="z. B. 1,20 m oder 120 cm"
                                    onChange={(e) => { setLaenge(e.target.value); setErfolg(null); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void absenden(); } }}
                                    disabled={gesperrt}
                                    aria-invalid={zeigeLaengeFehler || undefined}
                                    className={INPUT}
                                />
                                <p className="text-[11px] text-slate-500 mt-1 tabular-nums" aria-live="polite">
                                    {zeigeMm !== null ? `= ${mm(zeigeMm)} mm` : 'z. B. 1,20 m oder 120 cm'}
                                </p>
                                {zeigeLaengeFehler && <p className="text-xs text-red-700 mt-0.5">{laengeGeprueft.fehler}</p>}
                            </div>
                            <div>
                                <span className={LABEL}>Kopien</span>
                                <div role="group" aria-label="Anzahl der Kopien" className="inline-flex items-center rounded-xl border border-slate-300 bg-white overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => { setKopien(k => Math.max(1, k - 1)); setErfolg(null); }}
                                        disabled={gesperrt || zeigeKopien <= 1}
                                        aria-label="Eine Kopie weniger"
                                        className="min-w-[44px] min-h-[40px] flex items-center justify-center text-slate-600 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent"
                                    >
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <span aria-live="polite" className="min-w-[3rem] text-center text-base font-bold tabular-nums text-slate-800">{zeigeKopien}</span>
                                    <button
                                        type="button"
                                        onClick={() => { setKopien(k => Math.min(PLOT_NACHTRAG_MAX_KOPIEN, k + 1)); setErfolg(null); }}
                                        disabled={gesperrt || zeigeKopien >= PLOT_NACHTRAG_MAX_KOPIEN}
                                        aria-label="Eine Kopie mehr"
                                        className="min-w-[44px] min-h-[40px] flex items-center justify-center text-slate-600 hover:bg-slate-100 active:bg-slate-200 disabled:opacity-30 disabled:hover:bg-transparent"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label htmlFor={idDatum} className={LABEL}>Gedruckt am</label>
                            <input
                                id={idDatum}
                                type="date"
                                value={zeigeDatum}
                                min={frueheste}
                                max={heute}
                                onChange={(e) => { setDatum(e.target.value); setErfolg(null); }}
                                disabled={gesperrt}
                                aria-invalid={(!gesperrt && !!datumFehler) || undefined}
                                className={`${INPUT} sm:w-48`}
                            />
                            {!gesperrt && datumFehler && <p className="text-xs text-red-700 mt-1">{datumFehler}</p>}
                        </div>

                        <div>
                            <label htmlFor={idBemerkung} className={LABEL}>Bemerkung (optional)</label>
                            <textarea
                                id={idBemerkung}
                                value={zeigeBemerkung}
                                maxLength={BEMERKUNG_MAX}
                                rows={2}
                                placeholder="z. B. alter Planstand, vom Büro-PC gedruckt"
                                onChange={(e) => { setBemerkung(e.target.value); setErfolg(null); }}
                                disabled={gesperrt}
                                className={`${INPUT} resize-y`}
                            />
                        </div>
                    </div>

                    {absicht && !absicht.laeuft && (
                        <div role="alert" className="p-3 rounded-xl border border-amber-200 bg-amber-50 space-y-1">
                            {absicht.fehler && <p className="text-sm text-red-700">{absicht.fehler}</p>}
                            <p className="text-xs text-slate-700">
                                Der letzte Nachtrag ist noch nicht bestätigt. „Erneut speichern“ schickt ihn noch einmal — gezählt wird er nur einmal.
                            </p>
                        </div>
                    )}
                    {erfolg && !absicht && (
                        <div role="status" className="space-y-1">
                            <p className="flex items-center gap-1.5 text-sm text-emerald-700">
                                <CheckCircle2 className="w-4 h-4 shrink-0" /> {erfolg.text}
                            </p>
                            {erfolg.schonDa && (
                                <p className="text-xs text-amber-800">Dieser Nachtrag war schon gespeichert — es gelten die damals gesendeten Angaben.</p>
                            )}
                        </div>
                    )}

                    {laeuft ? (
                        <Button variant="primary" size="lg" className="w-full min-h-[44px]" loading>
                            Wird gespeichert …
                        </Button>
                    ) : absicht ? (
                        <div className="space-y-1.5">
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button variant="primary" size="lg" className="flex-1 min-h-[44px]" onClick={() => { void absenden(); }}
                                    icon={<RotateCcw className="w-4 h-4" />}>
                                    Erneut speichern
                                </Button>
                                <Button variant="secondary" size="lg" className="min-h-[44px]" onClick={verwerfen} disabled={!absicht.verwerfbar}>
                                    Verwerfen
                                </Button>
                            </div>
                            {!absicht.verwerfbar && (
                                <p className="text-[11px] text-slate-500">
                                    Verwerfen geht erst nach der Prüfung, ob der Nachtrag doch angekommen ist — frühestens
                                    {` ${NACHTRAG_VERWERFEN_FRIST_MS / 1000} `}Sekunden nach dem Fehlschlag; die Liste wird dann neu geladen.
                                </p>
                            )}
                        </div>
                    ) : (
                        <Button
                            variant="primary"
                            size="lg"
                            className="w-full min-h-[44px]"
                            onClick={() => { void absenden(); }}
                            disabled={versucht && !gueltig}
                            icon={<ClipboardPlus className="w-4 h-4" />}
                        >
                            {gesamtMm !== null ? `Nachtragen (${meterText(gesamtMm)})` : 'Nachtragen'}
                        </Button>
                    )}

                    <div className="pt-2 border-t border-slate-100">
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <h3 className="text-sm font-bold text-slate-700">Drucke dieses Projekts</h3>
                            <div className="flex items-center gap-1">
                                {(manuellLaedt || standMs > 0) && (
                                    <span className="text-[11px] text-slate-400 tabular-nums" aria-live="polite">
                                        {manuellLaedt ? 'Wird aktualisiert …' : `Stand ${uhrzeit(standMs)}`}
                                    </span>
                                )}
                                <button
                                    type="button"
                                    onClick={manuellAktualisieren}
                                    disabled={manuellLaedt}
                                    aria-label="Liste aktualisieren"
                                    title="Liste aktualisieren"
                                    className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:text-blue-500 disabled:hover:bg-transparent"
                                >
                                    <RefreshCw className={`w-4 h-4 ${manuellLaedt ? 'animate-spin' : ''}`} />
                                </button>
                            </div>
                        </div>
                        {listeFehler && <p className="text-xs text-red-700 mb-2">Liste nicht geladen: {listeFehler}</p>}
                        {zeilen === null ? (
                            !listeFehler && (
                                <p className="flex items-center gap-2 text-xs text-slate-400">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt …
                                </p>
                            )
                        ) : zeilen.length === 0 ? (
                            <p className="text-xs text-slate-400">Noch keine gezählten Drucke zu diesem Projekt.</p>
                        ) : (
                            <ul className="divide-y divide-slate-100 max-h-[min(18rem,40vh)] overflow-y-auto overscroll-contain pr-1 border-y border-slate-100">
                                {zeilen.map(z => {
                                    const n = z.nachtrag;
                                    const frage = n !== null && loeschenFrage === n.id;
                                    return (
                                        <li key={z.schluessel} className="py-2.5">
                                            <div className="flex items-start gap-2">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                                        <span className="text-xs text-slate-500 tabular-nums">{z.ms ? datumFormat.format(z.ms) : z.zeitpunkt}</span>
                                                        <span className="text-sm font-semibold text-slate-700 tabular-nums">
                                                            {z.kopien} × {meterText(z.laengeMm)} = {meterText(z.gesamtMm)}
                                                        </span>
                                                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${z.quelle === 'nachtrag' ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600'}`}>
                                                            {z.quelle === 'nachtrag' ? 'nachgetragen' : `App · ${z.status}`}
                                                        </span>
                                                    </div>
                                                    <p className="text-[11px] text-slate-500 mt-0.5 break-words">{z.titel}</p>
                                                    <p className="text-[11px] text-slate-400">
                                                        {z.wer || 'Unbekannt'}
                                                        {z.bemerkung ? ` · ${z.bemerkung}` : ''}
                                                    </p>
                                                </div>
                                                {n && !frage && darfLoeschen(n) && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { setLoeschenFehler(null); setLoeschenFrage(n.id); }}
                                                        aria-label="Nachtrag löschen"
                                                        title="Nachtrag löschen"
                                                        className="min-w-[40px] min-h-[40px] flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 shrink-0"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                            {frage && n && (
                                                <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                                                    <p className="text-xs text-red-800 flex-1 min-w-[12rem]">
                                                        Nachtrag löschen? Er zählt dann nicht mehr gegen die Rolle.
                                                    </p>
                                                    <Button variant="ghost" size="sm" onClick={() => setLoeschenFrage(null)} disabled={loescht === n.id}>Abbrechen</Button>
                                                    <Button variant="danger" size="sm" onClick={() => { void loeschen(n.id); }} loading={loescht === n.id}>Löschen</Button>
                                                </div>
                                            )}
                                            {n && loeschenFehler?.id === n.id && <p role="alert" className="text-xs text-red-700 mt-1">{loeschenFehler.text}</p>}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
